import React, { useEffect, useMemo, useState } from "react";
import EventCard from "../components/EventCard";
import { publicApi } from "../lib/api";
import { Calendar, ChevronDown, History } from "lucide-react";
import { useContent, renderHighlightedTitle } from "../lib/useContent";
import { PageSEO } from "../components/PageSEO";

// -----------------------------------------------------------------------------
// Ordinamento cronologico automatico:
//   1) Eventi in corso     (state='ongoing')  → badge "In corso"
//   2) Eventi futuri       (state='upcoming') ordinati dal più vicino al più lontano
//   3) Eventi conclusi     (state='past')     ordinati dal più recente al più vecchio,
//                           visibili solo se l'utente sceglie di mostrarli
//                           OPPURE se non esiste alcun evento in agenda.
//
// Il backend restituisce già ogni evento decorato con `state` (calcolato via
// start_at/end_at rispetto all'orario del server). Facciamo comunque un
// fallback client-side per robustezza (es. rete lenta / vecchie versioni API).
// -----------------------------------------------------------------------------

const FILTERS = [
  { value: "all", label: "In agenda" },
  { value: "week", label: "Questa settimana" },
  { value: "month", label: "Questo mese" },
];

const parseDate = (s) => (s ? new Date(s) : null);

const computeState = (e) => {
  if (e.state) return e.state; // fidati del backend se presente
  const now = new Date();
  const start = parseDate(e.start_at);
  if (!start) return "upcoming";
  const end = parseDate(e.end_at) || start;
  if (start <= now && now <= end) return "ongoing";
  if (start > now) return "upcoming";
  return "past";
};

const inNextDays = (date, days) => {
  const now = new Date();
  const end = new Date();
  end.setDate(end.getDate() + days);
  return date >= now && date <= end;
};

const inCurrentMonth = (date) => {
  const now = new Date();
  return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
};

const filterAgenda = (list, filter) => {
  if (filter === "all") return list;
  if (filter === "week") return list.filter((e) => inNextDays(new Date(e.start_at), 7));
  if (filter === "month") return list.filter((e) => inCurrentMonth(new Date(e.start_at)));
  return list;
};

const FilterPill = ({ active, label, onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`px-4 py-2 rounded-full text-sm border transition-colors ${
      active
        ? "bg-stone-950 text-white border-stone-950"
        : "bg-white text-stone-800 border-stone-200 hover:border-stone-400"
    }`}
  >
    {label}
  </button>
);

const EmptyState = ({ title, subtitle }) => (
  <div data-testid="events-empty" className="py-24 text-center">
    <Calendar className="w-8 h-8 mx-auto text-stone-400 mb-6" />
    <p className="font-display text-3xl text-stone-950">{title}</p>
    <p className="text-stone-500 mt-3">{subtitle}</p>
  </div>
);

const GridView = ({ items, testId }) => (
  <div data-testid={testId} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
    {items.map((e) => <EventCard key={e.id} event={e} />)}
  </div>
);

const ListView = ({ items, testId }) => (
  <div data-testid={testId}>
    {items.map((e) => <EventCard key={e.id} event={e} variant="row" />)}
  </div>
);

const Events = () => {
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("grid");
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPast, setShowPast] = useState(false);
  // Tick orologio: forza un re-render ogni 60s per aggiornare il raggruppamento
  // ongoing/upcoming/past senza dover attendere refresh manuale allo scadere di end_at.
  const [nowMs, setNowMs] = useState(Date.now());
  const { field, seo } = useContent("events");

  const pretitle = field("hero", "pretitle", "Eventi");
  const headline = field("hero", "headline", "Cosa succede a Sanluri.");
  const highlightTerms = field("hero", "highlight_terms", ["a Sanluri"]);
  const description = field("hero", "description", "Concerti, festival, mercati, mostre e incontri: la nostra agenda aggiornata degli eventi del centro storico.");
  const emptyTitle = field("empty_state", "title", "Nessun evento in programma.");
  const emptySubtitle = field("empty_state", "subtitle", "Torna a trovarci: aggiorniamo il calendario ogni settimana.");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    // include_past=true: chiediamo tutti gli eventi visibili, separiamo lato UI.
    publicApi.listEvents({ include_past: true, limit: 200 })
      .then((r) => { if (alive) setEvents(r.data || []); })
      .catch(() => { if (alive) setEvents([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Tick minutario per aggiornare lo stato live (In corso → Concluso allo scadere)
  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  // Decompose events by state (dipende da nowMs per aggiornarsi ogni minuto)
  const { ongoing, upcoming, past } = useMemo(() => {
    const groups = { ongoing: [], upcoming: [], past: [] };
    for (const ev of events) {
      const s = computeState(ev);
      groups[s]?.push({ ...ev, state: s });
    }
    // ordina: ongoing/upcoming per data ASC, past per data DESC
    groups.ongoing.sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
    groups.upcoming.sort((a, b) => new Date(a.start_at) - new Date(b.start_at));
    groups.past.sort((a, b) => new Date(b.start_at) - new Date(a.start_at));
    return groups;
  }, [events, nowMs]);

  // Agenda = ongoing + upcoming (con filtri periodo applicati solo agli upcoming)
  const agenda = useMemo(() => {
    const filteredUpcoming = filterAgenda(upcoming, filter);
    // gli "ongoing" restano sempre visibili in cima (indipendenti dal filtro periodo)
    return [...ongoing, ...filteredUpcoming];
  }, [ongoing, upcoming, filter]);

  const hasAgenda = agenda.length > 0;
  const hasPast = past.length > 0;
  // Se non ci sono eventi in agenda, mostriamo automaticamente i passati (auto-fallback).
  const showPastAuto = !loading && !hasAgenda && hasPast;
  const pastVisible = showPast || showPastAuto;

  const List = view === "grid" ? GridView : ListView;

  return (
    <div className="pt-32">
      <PageSEO seo={seo} defaults={{ title: "Eventi a Sanluri — CCN Sanluri", description: "Concerti, festival, mercati e mostre nel centro storico di Sanluri." }} />
      <section className="pb-8">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          {pretitle !== "" && <div className="text-xs uppercase tracking-[0.22em] text-slate-500 mb-6">{pretitle}</div>}
          <h1 className="font-display font-bold text-5xl md:text-7xl leading-[1.02] tracking-tight text-stone-950 max-w-4xl">
            {renderHighlightedTitle(headline, highlightTerms)}
          </h1>
          {description !== "" && (
            <p className="mt-8 max-w-2xl text-lg text-slate-600">{description}</p>
          )}
        </div>
      </section>

      <section className="py-6 border-y border-stone-200 bg-white/50">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-2 flex-wrap">
            {FILTERS.map((f) => (
              <FilterPill key={f.value} active={filter === f.value} label={f.label} onClick={() => setFilter(f.value)} testId={`filter-${f.value}`} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            <FilterPill active={view === "grid"} label="Griglia" onClick={() => setView("grid")} />
            <FilterPill active={view === "list"} label="Elenco" onClick={() => setView("list")} />
          </div>
        </div>
      </section>

      <section className="py-14">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 space-y-16">
          {loading && <div className="py-24 text-center text-slate-500">Caricamento eventi…</div>}

          {/* AGENDA principale: ongoing + upcoming */}
          {!loading && hasAgenda && (
            <div>
              {ongoing.length > 0 && (
                <div className="mb-4 text-sm text-[#12964A] font-medium">
                  {ongoing.length === 1 ? "1 evento in corso ora" : `${ongoing.length} eventi in corso ora`}
                </div>
              )}
              <List items={agenda} testId="events-agenda" />
            </div>
          )}

          {/* Nessun evento in agenda → mostra automaticamente i passati con avviso */}
          {!loading && !hasAgenda && !hasPast && (
            <EmptyState title={emptyTitle} subtitle={emptySubtitle} />
          )}
          {!loading && !hasAgenda && hasPast && (
            <div>
              <div data-testid="events-past-auto-notice" className="rounded-2xl border border-amber-200 bg-amber-50 text-amber-900 px-5 py-4 mb-8">
                <p className="font-medium">Al momento non ci sono eventi in programma.</p>
                <p className="text-sm mt-1 text-amber-800/80">Ecco gli ultimi eventi conclusi. Torna presto per gli aggiornamenti.</p>
              </div>
              <List items={past} testId="events-past" />
            </div>
          )}

          {/* Sezione "Eventi conclusi" toggle (solo se ci sono agenda + past) */}
          {!loading && hasAgenda && hasPast && (
            <div className="pt-6 border-t border-stone-200">
              <button
                type="button"
                data-testid="toggle-past-events"
                onClick={() => setShowPast((v) => !v)}
                className="inline-flex items-center gap-2 text-sm text-stone-700 hover:text-[#12964A] transition-colors"
              >
                <History className="w-4 h-4" />
                {showPast ? "Nascondi eventi conclusi" : `Mostra eventi conclusi (${past.length})`}
                <ChevronDown className={`w-4 h-4 transition-transform ${showPast ? "rotate-180" : ""}`} />
              </button>
              {pastVisible && (
                <div className="mt-8">
                  <h2 className="font-display text-2xl md:text-3xl text-stone-950 mb-6">Eventi conclusi</h2>
                  <List items={past} testId="events-past" />
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Events;
